/**
 * Session — the signed-in caller's actor projection. Pure state: every
 * write is the reconciler's fold of Firebase Auth + Firestore
 * (session.sync.ts, web-side) — no I/O lives here. The sign-in flow's own
 * screen progress and command channel live in sign.state.ts; this file
 * tracks only the resolved identity.
 */
import { computed, state } from "@mhmo91/schmancy/state";
import type { Workspace } from "@momo/types";
import { Subject } from "rxjs";

// No "signed-out" member: sign-out lands on "anonymous" via the reconciler's
// anonymous re-bootstrap — a session is never resolved-but-absent.
//
// "error" is the terminal state of a persistently-failing anonymous bootstrap:
// the reconciler retries with backoff, and only after a bounded give-up does it
// land here, so a rate-limited or unreachable auth endpoint stops hammering and
// the app shell has a status to render a visible "couldn't connect" curtain
// against instead of looping behind the splash forever.
//
// "binding" is a signed-in person whose organization changed and whose role
// there has not answered: the actor is unanswered, so every permission reads
// closed, and the admin guard waits rather than redirecting.
export type SessionStatus =
	| "loading"
	| "anonymous"
	| "binding"
	| "member"
	| "error";

export type SessionState = {
	readonly status: SessionStatus;
	readonly uid: string | null;
	readonly email: string | null;
	readonly needsConsent: boolean;
	readonly actor: Workspace.Actor | null;
};

const initial: SessionState = {
	status: "loading",
	uid: null,
	email: null,
	needsConsent: false,
	actor: null,
};

export const session = state("admin/session").memory(initial);

// Whether a non-anonymous person is signed in, apart from the actor that
// later binds to them; null until the auth listener first answers. Kept on
// clear: a member-to-member handover clears every cell yet leaves this true,
// and the listener writes only when the answer changes.
export const signedIn = state<boolean | null>("auth/signed-in").memory(null, {
	keepOnClear: true,
});

// Read at two independent sites — the admin-shell route guard (app-index.view.ts,
// web-side) and the sign-in view's signed-in event (sign-in.view.ts) — so
// both agree on exactly one definition of "sign-in is complete" rather than
// each restating it.
export const sessionReady = computed(
	() =>
		session.value.status === "member" &&
		!session.value.needsConsent &&
		session.value.actor?.mfa === true,
);

// The manual-retry channel out of the "error" curtain (session-error.view.ts),
// mirroring signAction$: the view stays I/O-free and just calls .next(), while
// session.sync.ts (web-side) is the sole subscriber that re-drives the
// anonymous bootstrap. A fresh backoff cycle runs per retry, so the user is
// never stranded once the automatic give-up has fired.
export const sessionRetry$ = new Subject<void>();
